import { useState, useEffect } from "react";
import { Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Home,
  Package,
  Users,
  Building2,
  LogOut,
  Menu,
  X,
  CheckCircle,
  Clock,
  MapPin,
  Phone,
  User,
  Mail,
  Ban,
  Trash2,
  UserCheck,
  XCircle,
  BarChart3,
  FileText,
  Settings,
  MessageCircle,
  DollarSign
} from "lucide-react";
import { API } from "@/App";
import { PendingCompanies, AdminCreate, ExportButtons } from "./AdminManagement";
import AdminSettings from "./AdminSettings";

const CarpetLogo = ({ className }) => (
  <svg className={className} viewBox="0 0 40 40" fill="none">
    <rect x="4" y="6" width="32" height="28" rx="3" fill="#F97316" />
    <rect x="8" y="10" width="24" height="20" rx="2" fill="white" />
    <rect x="12" y="14" width="16" height="12" rx="1" fill="#F97316" />
    <circle cx="20" cy="20" r="3" fill="white" />
  </svg>
);

const STATUS_MAP = {
  pending: { label: "Bekliyor", color: "bg-amber-100 text-amber-800" },
  assigned: { label: "Atandı", color: "bg-blue-100 text-blue-800" },
  picked_up: { label: "Alındı", color: "bg-indigo-100 text-indigo-800" },
  washing: { label: "Yıkanıyor", color: "bg-purple-100 text-purple-800" },
  ready: { label: "Hazır", color: "bg-emerald-100 text-emerald-800" },
  delivered: { label: "Teslim Edildi", color: "bg-green-100 text-green-800" },
  cancelled: { label: "İptal Edildi", color: "bg-red-100 text-red-800" }
};

const CARPET_TYPES = {
  makine: { name: "Makine Halısı" },
  shaggy: { name: "Shaggy Halı" },
  bambu: { name: "Bambu Halı" },
  viskon: { name: "Viskon Halı" },
  akrilik: { name: "Akrilik Halı" },
  kilim: { name: "Kilim" },
  el_dokuma: { name: "El Dokuma" },
  yun: { name: "Yün Halı" },
  isparta: { name: "Isparta Halısı" },
  hasir: { name: "Hasır Halı" },
  hereke: { name: "Hereke Halısı" },
  ladik: { name: "Ladik Halısı" },
  step: { name: "Step Halı" },
  usak: { name: "Uşak Halısı" },
  cin: { name: "Çin Halısı" },
  afgan: { name: "Afgan Halısı" },
  bunyan: { name: "Bünyan Halı" },
  iran: { name: "İran Halısı" },
  ipek: { name: "İpek Halı" },
  nepal: { name: "Nepal Halısı" },
  deri: { name: "Deri Halı" },
  patchwork: { name: "Patchwork" },
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const Sidebar = ({ user, onLogout, mobileOpen, setMobileOpen }) => {
  const location = useLocation();
  const links = [
    { path: "/admin", icon: <Home className="w-5 h-5" />, label: "Ana Sayfa" },
    { path: "/admin/orders", icon: <Package className="w-5 h-5" />, label: "Siparişler" },
    { path: "/admin/reports", icon: <BarChart3 className="w-5 h-5" />, label: "Raporlar" },
    { path: "/admin/pending-companies", icon: <Building2 className="w-5 h-5" />, label: "Onay Bekleyen Firmalar" },
    { path: "/admin/companies", icon: <Building2 className="w-5 h-5" />, label: "Firmalar" },
    { path: "/admin/users", icon: <Users className="w-5 h-5" />, label: "Müşteriler" },
    { path: "/admin/create", icon: <FileText className="w-5 h-5" />, label: "Yeni Oluştur" },
    { path: "/admin/settings", icon: <Settings className="w-5 h-5" />, label: "Ayarlar" }
  ];

  const SidebarContent = () => (
    <>
      <div className="p-6 border-b border-stone-200">
        <div className="flex items-center gap-2">
          <CarpetLogo className="w-10 h-10" />
          <div>
            <span className="text-xl font-bold text-slate-800">HALIYOL</span>
            <p className="text-xs text-slate-500">Admin Paneli</p>
          </div>
        </div>
      </div>
      <div className="p-4">
        <div className="bg-slate-100 rounded-xl p-4 mb-6">
          <p className="text-sm text-slate-500">Yönetici</p>
          <p className="font-semibold text-slate-800 truncate">{user?.name || "Admin"}</p>
        </div>
        <nav className="space-y-2">
          {links.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setMobileOpen(false)}
              className={`sidebar-link ${location.pathname === link.path ? "active" : ""}`}
            >
              {link.icon}
              {link.label}
            </Link>
          ))}
        </nav>
      </div>
      <div className="mt-auto p-4 border-t border-stone-200">
        <button onClick={onLogout} className="sidebar-link w-full text-red-600 hover:bg-red-50">
          <LogOut className="w-5 h-5" />
          Çıkış Yap
        </button>
      </div>
    </>
  );

  return (
    <>
      <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-stone-200 h-screen fixed left-0 top-0">
        <SidebarContent />
      </aside>
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-xl shadow-lg"
        onClick={() => setMobileOpen(!mobileOpen)}
      >
        {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>
      {mobileOpen && (
        <div className="mobile-menu" onClick={() => setMobileOpen(false)}>
          <aside className="mobile-menu-content flex flex-col" onClick={(e) => e.stopPropagation()}>
            <SidebarContent />
          </aside>
        </div>
      )}
    </>
  );
};

const AdminHome = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const r = await axios.get(`${API}/admin/stats`);
      setStats(r.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (loading)
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );

  return (
    <div className="space-y-8">
      <div className="bg-gradient-to-r from-orange-600 to-orange-700 rounded-3xl p-8 text-white">
        <h1 className="text-2xl md:text-3xl font-bold mb-2">Admin Paneli 🛡️</h1>
        <p className="text-orange-200">Platform yönetimi</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="stat-card">
          <Package className="w-8 h-8 text-orange-500" />
          <div className="stat-value">{stats?.total_orders || 0}</div>
          <div className="stat-label">Toplam Sipariş</div>
        </div>
        <div className="stat-card">
          <Clock className="w-8 h-8 text-amber-500" />
          <div className="stat-value">{stats?.pending_orders || 0}</div>
          <div className="stat-label">Havuzda</div>
        </div>
        <div className="stat-card">
          <CheckCircle className="w-8 h-8 text-green-500" />
          <div className="stat-value">{stats?.completed_orders || 0}</div>
          <div className="stat-label">Tamamlanan</div>
        </div>
        <div className="stat-card">
          <XCircle className="w-8 h-8 text-red-500" />
          <div className="stat-value">{stats?.cancelled_orders || 0}</div>
          <div className="stat-label">İptal</div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="dashboard-card bg-gradient-to-br from-orange-500 to-orange-600 text-white">
          <Users className="w-10 h-10 mb-4 opacity-80" />
          <p className="text-sm opacity-80">Toplam Müşteri</p>
          <p className="text-3xl font-bold">{stats?.total_customers || 0}</p>
        </div>
        <div className="dashboard-card bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
          <Building2 className="w-10 h-10 mb-4 opacity-80" />
          <p className="text-sm opacity-80">Kayıtlı Firma</p>
          <p className="text-3xl font-bold">{stats?.total_companies || 0}</p>
        </div>
      </div>

      <div className="dashboard-card">
        <h2 className="text-xl font-bold text-slate-800 mb-4">Hızlı Erişim</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          <Link to="/admin/orders">
            <Button variant="outline" className="w-full py-6 rounded-xl border-stone-300">
              <Package className="w-5 h-5 mr-2" />
              Siparişler
            </Button>
          </Link>
          <Link to="/admin/reports">
            <Button variant="outline" className="w-full py-6 rounded-xl border-stone-300">
              <BarChart3 className="w-5 h-5 mr-2" />
              Raporlar
            </Button>
          </Link>
          <Link to="/admin/companies">
            <Button variant="outline" className="w-full py-6 rounded-xl border-stone-300">
              <Building2 className="w-5 h-5 mr-2" />
              Firmalar
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

const CARPET_TYPE_LIST = [
  { key: "makine", label: "Makine Halısı" },
  { key: "shaggy", label: "Shaggy Halı" },
  { key: "bambu", label: "Bambu Halı" },
  { key: "viskon", label: "Viskon Halı" },
  { key: "akrilik", label: "Akrilik Halı" },
  { key: "kilim", label: "Kilim" },
  { key: "el_dokuma", label: "El Dokuma" },
  { key: "yun", label: "Yün Halı" },
  { key: "isparta", label: "Isparta Halısı" },
  { key: "hasir", label: "Hasır Halı" },
  { key: "hereke", label: "Hereke Halısı" },
  { key: "ladik", label: "Ladik Halısı" },
  { key: "step", label: "Step Halı" },
  { key: "usak", label: "Uşak Halısı" },
  { key: "cin", label: "Çin Halısı" },
  { key: "afgan", label: "Afgan Halısı" },
  { key: "bunyan", label: "Bünyan Halı" },
  { key: "iran", label: "İran Halısı" },
  { key: "ipek", label: "İpek Halı" },
  { key: "nepal", label: "Nepal Halısı" },
  { key: "deri", label: "Deri Halı" },
  { key: "patchwork", label: "Patchwork" },
];

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignModal, setAssignModal] = useState({ open: false, order: null });
  const [cancelModal, setCancelModal] = useState({ open: false, order: null });
  const [priceModal, setPriceModal] = useState({ open: false, order: null });
  const [newPrice, setNewPrice] = useState("");
  const [priceSaving, setPriceSaving] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [cancelReason, setCancelReason] = useState("");
  const [statusModal, setStatusModal] = useState({ open: false, order: null });
  const [carpetModal, setCarpetModal] = useState({ open: false, order: null });
  const [newStatus, setNewStatus] = useState("");
  const [statusSaving, setStatusSaving] = useState(false);
  const [carpetRows, setCarpetRows] = useState([{ carpet_type: "makine", area: "" }]);
  const [carpetSaving, setCarpetSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  // ✅ WhatsApp helpers
  const normalizePhoneTR = (phone) => {
    const digits = String(phone || "").replace(/\D/g, "");
    if (!digits) return "";
    if (digits.startsWith("90")) return digits; // 905xx...
    if (digits.startsWith("0")) return `9${digits}`; // 05xx... -> 905xx...
    if (digits.startsWith("5")) return `90${digits}`; // 5xx... -> 905xx...
    return digits;
  };

  const buildWpMessage = (order) => {
    const statusLabel = STATUS_MAP?.[order.status]?.label || order.status;

    const estimated =
      (order.carpets || [])
        .map(
          (c) =>
            `• ${(c.area ?? 0).toFixed(1)} m² ${CARPET_TYPES[c.carpet_type]?.name || c.carpet_type}`
        )
        .join("\n") || "• -";

    const actual = (order.actual_carpets || [])
      .map(
        (c) =>
          `• ${(c.area ?? 0).toFixed(1)} m² ${CARPET_TYPES[c.carpet_type]?.name || c.carpet_type}`
      )
      .join("\n");

    const lines = [
      "HALIYOL – Sipariş Bilgilendirme",
      `Sipariş No: ${order.order_id}`,
      `Durum: ${statusLabel}`,
      "",
      `Müşteri: ${order.customer_name || "-"}`,
      `Telefon: ${order.customer_phone || "-"}`,
      `Adres: ${order.customer_address ? order.customer_address + ", " : ""}${order.district || "-"}, ${order.city || "-"}`,
      "",
      "Halılar (Tahmini):",
      estimated
    ];

    if (actual) {
      lines.push("", "Halılar (Gerçek):", actual);
      if (order.actual_total_price != null)
        lines.push(`Toplam Tutar: ${Number(order.actual_total_price).toFixed(0)} ₺`);
    }

    lines.push(
      "",
      `Sipariş: ${formatDate(order.created_at)}`,
      `Alındı: ${formatDate(order.pickup_date)}`,
      `Yıkama: ${formatDate(order.washing_date)}`,
      `Teslim: ${formatDate(order.delivery_date)}`
    );

    return lines.join("\n");
  };

  const sendWp = (order) => {
    const phone = normalizePhoneTR(order.customer_phone);
    const text = encodeURIComponent(buildWpMessage(order));
    const url = phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handlePriceSave = async () => {
    if (newPrice === "" || isNaN(parseFloat(newPrice))) {
      toast.error("Geçerli bir fiyat girin");
      return;
    }
    setPriceSaving(true);
    try {
      await axios.patch(`${API}/admin/orders/${priceModal.order.order_id}/price`, {
        actual_total_price: parseFloat(newPrice)
      });
      toast.success("Fiyat güncellendi");
      setPriceModal({ open: false, order: null });
      setNewPrice("");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fiyat güncellenemedi");
    } finally {
      setPriceSaving(false);
    }
  };

  const fetchData = async () => {
    try {
      const [o, c] = await Promise.all([axios.get(`${API}/orders`), axios.get(`${API}/admin/companies`)]);
      setOrders(o.data.orders);
      setCompanies(c.data.companies);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedCompany) {
      toast.error("Lütfen bir firma seçin");
      return;
    }
    try {
      await axios.post(`${API}/orders/${assignModal.order.order_id}/assign`, { company_id: selectedCompany });
      toast.success("Sipariş atandı");
      setAssignModal({ open: false, order: null });
      setSelectedCompany("");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Atama başarısız");
    }
  };

  const handleCancel = async () => {
    try {
      await axios.post(`${API}/orders/${cancelModal.order.order_id}/cancel`, { reason: cancelReason });
      toast.success("Sipariş iptal edildi");
      setCancelModal({ open: false, order: null });
      setCancelReason("");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "İptal başarısız");
    }
  };

  const handleStatusUpdate = async () => {
    if (!newStatus) return;
    setStatusSaving(true);
    try {
      await axios.patch(`${API}/orders/${statusModal.order.order_id}/status`, { status: newStatus });
      toast.success("Durum güncellendi");
      setStatusModal({ open: false, order: null });
      setNewStatus("");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Durum güncellenemedi");
    } finally {
      setStatusSaving(false);
    }
  };

  const addCarpetRow = () => setCarpetRows(prev => [...prev, { carpet_type: "makine", area: "" }]);
  const removeCarpetRow = (idx) => setCarpetRows(prev => prev.filter((_, i) => i !== idx));
  const updateCarpetRow = (idx, field, value) => {
    setCarpetRows(prev => prev.map((row, i) => i === idx ? { ...row, [field]: value } : row));
  };

  const handleCarpetSave = async () => {
    const valid = carpetRows.filter(r => r.area !== "" && parseFloat(r.area) > 0);
    if (valid.length === 0) {
      toast.error("En az bir halı giriniz");
      return;
    }
    setCarpetSaving(true);
    try {
      await axios.post(`${API}/orders/${carpetModal.order.order_id}/update-carpets`, {
        carpets: valid.map(r => ({ carpet_type: r.carpet_type, area: parseFloat(r.area) }))
      });
      toast.success("Halı bilgisi kaydedildi");
      setCarpetModal({ open: false, order: null });
      setCarpetRows([{ carpet_type: "makine", area: "" }]);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kayıt başarısız");
    } finally {
      setCarpetSaving(false);
    }
  };

  if (loading)
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Tüm Siparişler ({orders.length})</h1>
        <ExportButtons />
      </div>

      {orders.length === 0 ? (
        <div className="text-center py-12 bg-stone-50 rounded-2xl">
          <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Sipariş bulunmuyor</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => (
            <div key={order.order_id} className="dashboard-card">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="space-y-2 flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="font-bold text-slate-800">{order.order_id}</h3>
                    <Badge className={STATUS_MAP[order.status]?.color}>{STATUS_MAP[order.status]?.label}</Badge>
                  </div>
                  <div className="text-sm text-slate-600 space-y-1">
                    <p className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      {order.customer_name} - {order.customer_phone}
                    </p>
                    <p className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      {order.customer_address ? `${order.customer_address}, ` : ""}{order.district}, {order.city}
                    </p>
                    {order.company_name && (
                      <p className="flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        Firma: {order.company_name}
                      </p>
                    )}
                  </div>

                  {/* Halı Detayları */}
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className="text-xs text-slate-400">Tahmini:</span>
                    {order.carpets?.map((c, i) => (
                      <span key={i} className="text-xs bg-stone-100 px-2 py-1 rounded">
                        {c.area?.toFixed(1)} m² {CARPET_TYPES[c.carpet_type]?.name}
                      </span>
                    ))}
                  </div>

                  {order.actual_carpets?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-1">
                      <span className="text-xs text-green-600">Gerçek:</span>
                      {order.actual_carpets.map((c, i) => (
                        <span key={i} className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                          {c.area?.toFixed(1)} m² {CARPET_TYPES[c.carpet_type]?.name}
                        </span>
                      ))}
                      <span className="text-xs font-bold text-green-700 ml-2">{order.actual_total_price?.toFixed(0)} ₺</span>
                      {order.price_manually_set && (
                        <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">elle girildi</span>
                      )}
                    </div>
                  )}

                  {/* Fiyat Satırı - her zaman göster */}
                  {order.actual_total_price > 0 && order.actual_carpets?.length === 0 && (
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-orange-600 font-bold">{order.actual_total_price?.toFixed(0)} ₺</span>
                      {order.price_manually_set && (
                        <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">elle girildi</span>
                      )}
                    </div>
                  )}

                  {/* Tarihler */}
                  <div className="mt-3 pt-3 border-t border-stone-100 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    <div>
                      <span className="text-slate-400">Sipariş:</span>
                      <br />
                      <span className="font-medium">{formatDate(order.created_at)}</span>
                    </div>
                    <div>
                      <span className="text-slate-400">Alındı:</span>
                      <br />
                      <span className="font-medium">{formatDate(order.pickup_date)}</span>
                    </div>
                    <div>
                      <span className="text-slate-400">Yıkama:</span>
                      <br />
                      <span className="font-medium">{formatDate(order.washing_date)}</span>
                    </div>
                    <div>
                      <span className="text-slate-400">Teslim:</span>
                      <br />
                      <span className="font-medium">{formatDate(order.delivery_date)}</span>
                    </div>
                  </div>

                  {order.cancel_reason && <p className="text-xs text-red-600 mt-2">İptal Sebebi: {order.cancel_reason}</p>}
                </div>

                <div className="flex flex-col gap-2">
                  {/* ✅ WhatsApp Button */}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => sendWp(order)}
                    className="border-green-200 text-green-700 hover:bg-green-50"
                  >
                    <MessageCircle className="w-4 h-4 mr-1" />
                    WhatsApp Gönder
                  </Button>

                  {/* Fiyat Güncelle Butonu */}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setPriceModal({ open: true, order });
                      setNewPrice(order.actual_total_price > 0 ? String(order.actual_total_price) : "");
                    }}
                    className="border-orange-200 text-orange-700 hover:bg-orange-50"
                  >
                    💰 Fiyat Güncelle
                  </Button>

                  {order.status === "pending" && (
                    <Button
                      size="sm"
                      onClick={() => {
                        setAssignModal({ open: true, order });
                        setSelectedCompany("");
                      }}
                      className="bg-orange-500 hover:bg-orange-600"
                    >
                      Firmaya Ata
                    </Button>
                  )}
                  {!["delivered", "cancelled"].includes(order.status) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setStatusModal({ open: true, order });
                        setNewStatus(order.status);
                      }}
                      className="border-blue-200 text-blue-700 hover:bg-blue-50"
                    >
                      🔄 Durum Değiştir
                    </Button>
                  )}
                  {order.status === "assigned" && (!order.actual_carpets || order.actual_carpets.length === 0) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setCarpetModal({ open: true, order });
                        setCarpetRows([{ carpet_type: "makine", area: "" }]);
                      }}
                      className="border-purple-200 text-purple-700 hover:bg-purple-50"
                    >
                      🧺 Halı Bilgisi Gir
                    </Button>
                  )}
                  {!["delivered", "cancelled"].includes(order.status) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCancelModal({ open: true, order })}
                      className="text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      İptal Et
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Atama Modal */}
      <Dialog open={assignModal.open} onOpenChange={(open) => setAssignModal({ ...assignModal, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sipariş Atama</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-4">
              <strong>{assignModal.order?.order_id}</strong> siparişini atayın.
            </p>
            <Select value={selectedCompany} onValueChange={setSelectedCompany}>
              <SelectTrigger>
                <SelectValue placeholder="Firma Seçin" />
              </SelectTrigger>
              <SelectContent>
                {companies
                  .filter((c) => c.is_active)
                  .map((c) => (
                    <SelectItem key={c.user_id} value={c.user_id}>
                      {c.company_name} ({c.city})
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignModal({ open: false, order: null })}>
              İptal
            </Button>
            <Button onClick={handleAssign} className="bg-orange-500 hover:bg-orange-600">
              Ata
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* İptal Modal */}
      <Dialog open={cancelModal.open} onOpenChange={(open) => setCancelModal({ ...cancelModal, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sipariş İptali</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600 mb-4">
              <strong>{cancelModal.order?.order_id}</strong> siparişini iptal edin.
            </p>
            <Label>İptal Sebebi</Label>
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Sebep..."
              className="mt-1"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelModal({ open: false, order: null })}>
              Vazgeç
            </Button>
            <Button onClick={handleCancel} className="bg-red-500 hover:bg-red-600">
              İptal Et
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Durum Güncelleme Modal */}
      <Dialog open={statusModal.open} onOpenChange={(open) => { if (!open) { setStatusModal({ open: false, order: null }); setNewStatus(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sipariş Durumunu Güncelle</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-slate-600">
              Sipariş: <strong>{statusModal.order?.order_id}</strong> — {statusModal.order?.customer_name}
            </p>
            <div>
              <Label className="text-sm font-semibold">Yeni Durum</Label>
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-stone-200 rounded-lg text-sm bg-white"
              >
                {Object.entries(STATUS_MAP).filter(([k]) => k !== "cancelled").map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
            {newStatus === "picked_up" && (!statusModal.order?.actual_carpets || statusModal.order?.actual_carpets.length === 0) && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
                ⚠️ Halı bilgisi girilmeden "Alındı" durumuna geçilemez. Önce halı bilgisi girin.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setStatusModal({ open: false, order: null }); setNewStatus(""); }}>
              Vazgeç
            </Button>
            <Button
              onClick={handleStatusUpdate}
              disabled={statusSaving || (newStatus === "picked_up" && (!statusModal.order?.actual_carpets || statusModal.order?.actual_carpets.length === 0))}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {statusSaving ? "Kaydediliyor..." : "Güncelle"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Halı Bilgisi Girme Modal */}
      <Dialog open={carpetModal.open} onOpenChange={(open) => { if (!open) { setCarpetModal({ open: false, order: null }); setCarpetRows([{ carpet_type: "makine", area: "" }]); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>🧺 Halı Bilgisi Gir</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-slate-600">
              Sipariş: <strong>{carpetModal.order?.order_id}</strong> — {carpetModal.order?.customer_name}
            </p>
            <div className="space-y-3">
              {carpetRows.map((row, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <select
                    value={row.carpet_type}
                    onChange={(e) => updateCarpetRow(idx, "carpet_type", e.target.value)}
                    className="flex-1 px-3 py-2 border border-stone-200 rounded-lg text-sm bg-white"
                  >
                    {CARPET_TYPE_LIST.map(({ key, label }) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                  <Input
                    type="number"
                    min="0"
                    step="0.1"
                    placeholder="m²"
                    value={row.area}
                    onChange={(e) => updateCarpetRow(idx, "area", e.target.value)}
                    className="w-24"
                  />
                  <span className="text-xs text-slate-500">m²</span>
                  {carpetRows.length > 1 && (
                    <button onClick={() => removeCarpetRow(idx)} className="text-red-400 hover:text-red-600 text-lg leading-none">×</button>
                  )}
                </div>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={addCarpetRow} className="w-full border-dashed border-slate-300 text-slate-500">
              + Halı Ekle
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCarpetModal({ open: false, order: null }); setCarpetRows([{ carpet_type: "makine", area: "" }]); }}>
              Vazgeç
            </Button>
            <Button onClick={handleCarpetSave} disabled={carpetSaving} className="bg-purple-600 hover:bg-purple-700">
              {carpetSaving ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fiyat Güncelleme Modal */}
      <Dialog open={priceModal.open} onOpenChange={(open) => { if (!open) { setPriceModal({ open: false, order: null }); setNewPrice(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Müşteriden Alınacak Tutarı Güncelle</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-slate-600">
              Sipariş: <strong>{priceModal.order?.order_id}</strong> — {priceModal.order?.customer_name}
            </p>
            {priceModal.order?.actual_total_price > 0 && (
              <div className="flex items-center justify-between bg-stone-50 rounded px-3 py-2 text-sm">
                <span className="text-slate-500">Mevcut tutar:</span>
                <span className="font-bold text-slate-700">{priceModal.order?.actual_total_price?.toFixed(0)} ₺</span>
              </div>
            )}
            <div>
              <Label className="text-sm font-semibold">Yeni Tutar (₺)</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type="number"
                  step="1"
                  min="0"
                  value={newPrice}
                  onChange={(e) => setNewPrice(e.target.value)}
                  placeholder="Örn: 800"
                  className="border-orange-300 focus:border-orange-500"
                  autoFocus
                />
                <span className="text-slate-500 font-medium">₺</span>
              </div>
              <p className="text-xs text-slate-400 mt-1">İndirim uyguladıysanız gerçek alacağınız tutarı girin.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPriceModal({ open: false, order: null }); setNewPrice(""); }}>
              Vazgeç
            </Button>
            <Button onClick={handlePriceSave} disabled={priceSaving} className="bg-orange-500 hover:bg-orange-600">
              {priceSaving ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const AdminReports = () => {
  const [reports, setReports] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [period, setPeriod] = useState("monthly");
  const [selectedCompany, setSelectedCompany] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [useCustomDate, setUseCustomDate] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCompanies();
  }, []);
  useEffect(() => {
    fetchReports();
  }, [period, selectedCompany, startDate, endDate, useCustomDate]);

  const fetchCompanies = async () => {
    try {
      const r = await axios.get(`${API}/admin/companies`);
      setCompanies(r.data.companies);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/reports?period=${period}`;
      if (selectedCompany && selectedCompany !== "all") url += `&company_id=${selectedCompany}`;
      if (useCustomDate && startDate && endDate) {
        url += `&start=${startDate}T00:00:00Z&end=${endDate}T23:59:59Z`;
      }
      const r = await axios.get(url);
      setReports(r.data);
    } catch (e) {
      console.error(e);
      setReports(null);
    } finally {
      setLoading(false);
    }
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("tr-TR");
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-2">
        <BarChart3 className="w-6 h-6 text-orange-500" />
        Raporlar
      </h1>

      {/* Filtreler */}
      <div className="dashboard-card mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs text-slate-500">Dönem</Label>
            <Select
              value={period}
              onValueChange={(v) => {
                setPeriod(v);
                setUseCustomDate(false);
              }}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Günlük</SelectItem>
                <SelectItem value="weekly">Haftalık</SelectItem>
                <SelectItem value="monthly">Aylık</SelectItem>
                <SelectItem value="yearly">Yıllık</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Firma</Label>
            <Select value={selectedCompany} onValueChange={setSelectedCompany}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Tüm Firmalar" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Firmalar</SelectItem>
                {companies.map((c) => (
                  <SelectItem key={c.user_id} value={c.user_id}>
                    {c.company_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Başlangıç Tarihi</Label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setUseCustomDate(true);
              }}
              className="mt-1 w-full px-3 py-2 border border-stone-200 rounded-lg text-sm"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Bitiş Tarihi</Label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setUseCustomDate(true);
              }}
              className="mt-1 w-full px-3 py-2 border border-stone-200 rounded-lg text-sm"
            />
          </div>
        </div>
        {reports && (
          <p className="text-xs text-slate-500 mt-3">
            Tarih Aralığı: {formatDateDisplay(reports.start_date)} - {formatDateDisplay(reports.end_date)}
          </p>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : !reports ? (
        <div className="text-center py-12 bg-stone-50 rounded-2xl">
          <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Rapor yüklenemedi</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Özet Kartları */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="dashboard-card bg-gradient-to-br from-orange-500 to-orange-600 text-white">
              <p className="text-sm opacity-80">Toplam Sipariş</p>
              <p className="text-3xl font-bold">{reports.total_orders || 0}</p>
            </div>
            <div className="dashboard-card bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
              <p className="text-sm opacity-80">Toplam Yıkanan Alan</p>
              <p className="text-3xl font-bold">{(reports.total_area || 0).toFixed(1)} m²</p>
            </div>
            <div className="dashboard-card bg-gradient-to-br from-purple-500 to-purple-600 text-white">
              <p className="text-sm opacity-80">Toplam Ciro</p>
              <p className="text-3xl font-bold">{(reports.total_gross_revenue || 0).toLocaleString("tr-TR")} ₺</p>
              <p className="text-xs opacity-80 mt-1">Müşteriden alınan</p>
            </div>
            <div className="dashboard-card bg-gradient-to-br from-blue-500 to-blue-600 text-white">
              <p className="text-sm opacity-80">Firma Bazlı Ciro</p>
              <p className="text-3xl font-bold">{(reports.total_company_revenue || 0).toLocaleString("tr-TR")} ₺</p>
              <p className="text-xs opacity-80 mt-1">Firmalara kalan</p>
            </div>
            <div className="dashboard-card bg-gradient-to-br from-green-500 to-green-600 text-white">
              <p className="text-sm opacity-80">Firmalardan Alacak</p>
              <p className="text-3xl font-bold">{(reports.total_receivable || 0).toLocaleString("tr-TR")} ₺</p>
              <p className="text-xs opacity-80 mt-1">Halıyol kazancı</p>
            </div>
          </div>

          {/* Halı Türüne Göre Detay */}
          <div className="dashboard-card">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Halı Türüne Göre Detay</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-200">
                    <th className="text-left py-3 px-2 text-slate-500 font-medium">Halı Türü</th>
                    <th className="text-right py-3 px-2 text-slate-500 font-medium">Alan (m²)</th>
                    <th className="text-right py-3 px-2 text-slate-500 font-medium">Toplam Ciro (₺)</th>
                    <th className="text-right py-3 px-2 text-slate-500 font-medium">Firma Ciro (₺)</th>
                    <th className="text-right py-3 px-2 text-slate-500 font-medium">Alacak (₺)</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(CARPET_TYPES).map(([key, { name }]) => (
                    <tr key={key} className="border-b border-stone-100">
                      <td className="py-3 px-2 text-slate-700">{name}</td>
                      <td className="py-3 px-2 text-right font-medium text-slate-800">
                        {(reports.carpet_stats?.[key]?.area || 0).toFixed(1)}
                      </td>
                      <td className="py-3 px-2 text-right font-medium text-purple-600">
                        {(reports.carpet_stats?.[key]?.gross || 0).toLocaleString("tr-TR")}
                      </td>
                      <td className="py-3 px-2 text-right font-medium text-blue-600">
                        {(reports.carpet_stats?.[key]?.company || 0).toLocaleString("tr-TR")}
                      </td>
                      <td className="py-3 px-2 text-right font-medium text-green-600">
                        {(reports.carpet_stats?.[key]?.receivable || 0).toLocaleString("tr-TR")}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-stone-50 font-bold">
                    <td className="py-3 px-2 text-slate-800">TOPLAM</td>
                    <td className="py-3 px-2 text-right text-slate-800">{(reports.total_area || 0).toFixed(1)}</td>
                    <td className="py-3 px-2 text-right text-purple-600">
                      {(reports.total_gross_revenue || 0).toLocaleString("tr-TR")}
                    </td>
                    <td className="py-3 px-2 text-right text-blue-600">
                      {(reports.total_company_revenue || 0).toLocaleString("tr-TR")}
                    </td>
                    <td className="py-3 px-2 text-right text-green-600">
                      {(reports.total_receivable || 0).toLocaleString("tr-TR")}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Firma Bazlı Detay */}
          {(!selectedCompany || selectedCompany === "all") && reports.company_stats?.length > 0 && (
            <div className="dashboard-card">
              <h2 className="text-lg font-semibold text-slate-800 mb-4">Firma Bazlı Detay</h2>
              <div className="space-y-4">
                {reports.company_stats.map((cs, i) => (
                  <div key={i} className="p-4 bg-stone-50 rounded-xl">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-bold text-slate-800">{cs.name}</h3>
                      <Badge className="bg-orange-100 text-orange-800">{cs.order_count} sipariş</Badge>
                    </div>
                    <div className="grid grid-cols-3 gap-4 mb-3">
                      <div className="text-center p-3 bg-purple-50 rounded-lg">
                        <p className="text-xs text-purple-600">Toplam Ciro</p>
                        <p className="font-bold text-purple-700">{(cs.total_gross_revenue || 0).toLocaleString("tr-TR")} ₺</p>
                      </div>
                      <div className="text-center p-3 bg-blue-50 rounded-lg">
                        <p className="text-xs text-blue-600">Firmaya Kalan</p>
                        <p className="font-bold text-blue-700">{(cs.total_company_revenue || 0).toLocaleString("tr-TR")} ₺</p>
                      </div>
                      <div className="text-center p-3 bg-green-50 rounded-lg">
                        <p className="text-xs text-green-600">Halıyol'a Ödenecek</p>
                        <p className="font-bold text-green-700">{(cs.total_receivable || 0).toLocaleString("tr-TR")} ₺</p>
                      </div>
                    </div>
                    <div className="flex justify-between items-center pt-3 border-t border-stone-200">
                      <span className="text-slate-600">Toplam Alan</span>
                      <span className="font-bold text-slate-800">{(cs.total_area || 0).toFixed(1)} m²</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AdminCompanies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [priceModal, setPriceModal] = useState({ open: false, company: null });
  const [priceData, setPriceData] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceSaving, setPriceSaving] = useState(false);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const r = await axios.get(`${API}/admin/companies`);
      setCompanies(r.data.companies);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (company) => {
    try {
      await axios.patch(`${API}/admin/companies/${company.user_id}`, { is_active: !company.is_active });
      toast.success(company.is_active ? "Firma pasif yapıldı" : "Firma aktif yapıldı");
      fetchCompanies();
    } catch (e) {
      toast.error("İşlem başarısız");
    }
  };

  const openPriceModal = async (company) => {
    setPriceModal({ open: true, company });
    setPriceLoading(true);
    setPriceData(null);
    try {
      const r = await axios.get(`${API}/admin/companies/${company.user_id}/prices`);
      setPriceData(r.data);
    } catch (e) {
      toast.error("Fiyatlar yüklenemedi");
      setPriceModal({ open: false, company: null });
    } finally {
      setPriceLoading(false);
    }
  };

  const handlePriceChange = (key, value) => {
    setPriceData(prev => ({
      ...prev,
      prices: { ...prev.prices, [key]: parseFloat(value) || 0 }
    }));
  };

  const resetToDefault = (key) => {
    setPriceData(prev => ({
      ...prev,
      prices: { ...prev.prices, [key]: prev.default_prices[key] }
    }));
  };

  const resetAllToDefault = () => {
    setPriceData(prev => ({
      ...prev,
      prices: { ...prev.default_prices }
    }));
  };

  const savePrices = async () => {
    setPriceSaving(true);
    try {
      await axios.post(`${API}/admin/companies/${priceModal.company.user_id}/prices`, {
        prices: priceData.prices
      });
      toast.success(`${priceModal.company.company_name} fiyatları kaydedildi`);
      setPriceModal({ open: false, company: null });
    } catch (e) {
      toast.error("Kayıt başarısız");
    } finally {
      setPriceSaving(false);
    }
  };

  const CARPET_NAMES = {
    makine: "Makine Halısı", shaggy: "Shaggy Halı", bambu: "Bambu Halı",
    viskon: "Viskon Halı", akrilik: "Akrilik Halı", kilim: "Kilim",
    el_dokuma: "El Dokuma", yun: "Yün Halı", isparta: "Isparta Halısı",
    hasir: "Hasır Halı", hereke: "Hereke Halısı", ladik: "Ladik Halısı",
    step: "Step Halı", usak: "Uşak Halısı", cin: "Çin Halısı",
    afgan: "Afgan Halısı", bunyan: "Bünyan Halı", iran: "İran Halısı",
    ipek: "İpek Halı", nepal: "Nepal Halısı", deri: "Deri Halı",
    patchwork: "Patchwork"
  };

  if (loading)
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Firmalar ({companies.length})</h1>
        <ExportButtons />
      </div>
      {companies.length === 0 ? (
        <div className="text-center py-12 bg-stone-50 rounded-2xl">
          <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Firma bulunmuyor</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {companies.map((company) => (
            <div key={company.user_id} className="dashboard-card">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-bold text-slate-800 text-lg">{company.company_name}</h3>
                  <p className="text-sm text-slate-500">{company.city}</p>
                </div>
                <Badge className={company.is_active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
                  {company.is_active ? "Aktif" : "Pasif"}
                </Badge>
              </div>
              <div className="space-y-2 text-sm text-slate-600">
                <p className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  {company.email}
                </p>
                {company.phone && (
                  <p className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    {company.phone}
                  </p>
                )}
              </div>
              <div className="mt-4 pt-4 border-t border-stone-100">
                <p className="text-sm text-slate-600">
                  Toplam Yıkanan:{" "}
                  <span className="font-bold text-orange-600">{(company.total_area_washed || 0).toFixed(1)} m²</span>
                </p>
                {company.prices_updated_at && (
                  <p className="text-xs text-slate-400 mt-1">
                    Fiyat güncellendi: {new Date(company.prices_updated_at).toLocaleDateString("tr-TR")}
                  </p>
                )}
              </div>
              <div className="mt-4 flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openPriceModal(company)}
                  className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                >
                  <DollarSign className="w-4 h-4 mr-1" />
                  Anlaşma Fiyatları
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleActive(company)}
                  className={
                    company.is_active
                      ? "text-red-600 border-red-200 hover:bg-red-50"
                      : "text-green-600 border-green-200 hover:bg-green-50"
                  }
                >
                  {company.is_active ? "Pasif Yap" : "Aktif Yap"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Firma Anlaşma Fiyatları Modalı */}
      <Dialog open={priceModal.open} onOpenChange={(open) => !open && setPriceModal({ open: false, company: null })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-indigo-500" />
              {priceModal.company?.company_name} — Anlaşma Fiyatları
            </DialogTitle>
          </DialogHeader>

          {priceLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : priceData ? (
            <div className="py-2 space-y-4">
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-sm text-indigo-700">
                Bu firmaya özel anlaşma fiyatlarını girin (₺/m²). Boş bırakılan değerler varsayılan fiyatı kullanır.
                Raporlardaki "firmaya kalan" ve "Halıyol alacağı" tutarları bu fiyatlara göre hesaplanır.
              </div>

              <div className="flex justify-end">
                <Button variant="outline" size="sm" onClick={resetAllToDefault} className="text-slate-600 text-xs">
                  Tümünü Varsayılana Sıfırla
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {Object.entries(CARPET_NAMES).map(([key, name]) => {
                  const current = priceData.prices[key] ?? priceData.default_prices[key];
                  const isDefault = current === priceData.default_prices[key];
                  return (
                    <div key={key} className={`rounded-lg p-3 border ${isDefault ? "bg-stone-50 border-stone-200" : "bg-indigo-50 border-indigo-300"}`}>
                      <div className="flex items-center justify-between mb-1">
                        <Label className="text-xs text-slate-600">{name}</Label>
                        {!isDefault && (
                          <button onClick={() => resetToDefault(key)} className="text-xs text-slate-400 hover:text-indigo-600">
                            ↺ varsayılan ({priceData.default_prices[key]}₺)
                          </button>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={current}
                          onChange={(e) => handlePriceChange(key, e.target.value)}
                          className="w-full h-8 px-2 text-sm border border-stone-200 rounded bg-white"
                        />
                        <span className="text-xs text-slate-500 whitespace-nowrap">₺/m²</span>
                      </div>
                      {!isDefault && (
                        <p className="text-xs text-indigo-600 mt-1">
                          Fark: {current > priceData.default_prices[key] ? "+" : ""}{(current - priceData.default_prices[key]).toFixed(0)}₺
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => setPriceModal({ open: false, company: null })}>
              İptal
            </Button>
            <Button onClick={savePrices} disabled={priceSaving || priceLoading} className="bg-indigo-600 hover:bg-indigo-700">
              {priceSaving ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmModal, setConfirmModal] = useState({ open: false, type: "", user: null });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const r = await axios.get(`${API}/admin/users`);
      setUsers(r.data.users);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleBan = async (userId) => {
    try {
      const user = users.find((u) => u.user_id === userId);
      if (user?.is_banned) {
        await axios.post(`${API}/admin/users/${userId}/unban`);
        toast.success("Yasak kaldırıldı");
      } else {
        await axios.post(`${API}/admin/users/${userId}/ban`);
        toast.success("Kullanıcı yasaklandı");
      }
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "İşlem başarısız");
    }
    setConfirmModal({ open: false, type: "", user: null });
  };

  const handleDelete = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success("Kullanıcı silindi");
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Silme başarısız");
    }
    setConfirmModal({ open: false, type: "", user: null });
  };

  if (loading)
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );

  const customers = users.filter((u) => u.role === "customer");
  const companyUsers = users.filter((u) => u.role === "company");

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Kullanıcı Yönetimi</h1>
        <ExportButtons />
      </div>

      <h2 className="text-lg font-semibold text-slate-700 mb-4">Müşteriler ({customers.length})</h2>
      {customers.length === 0 ? (
        <div className="text-center py-8 bg-stone-50 rounded-2xl mb-8">
          <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Müşteri bulunmuyor</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {customers.map((user) => (
            <div key={user.user_id} className={`dashboard-card ${user.is_banned ? "border-red-200 bg-red-50" : ""}`}>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-orange-500" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-slate-800">{user.name}</h3>
                    {user.is_banned && <Badge className="bg-red-100 text-red-800">Yasaklı</Badge>}
                  </div>
                  <p className="text-sm text-slate-500">{user.email}</p>
                </div>
              </div>
              <div className="space-y-2 text-sm text-slate-600">
                {user.phone && (
                  <p className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    {user.phone}
                  </p>
                )}
                {user.city && (
                  <p className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {user.address ? `${user.address}, ` : ""}{user.district ? `${user.district}, ` : ""}{user.city}
                  </p>
                )}
              </div>
              <div className="mt-4 pt-4 border-t border-stone-100 flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmModal({ open: true, type: "ban", user })}
                  className={user.is_banned ? "text-green-600 border-green-200" : "text-amber-600 border-amber-200"}
                >
                  {user.is_banned ? (
                    <>
                      <UserCheck className="w-4 h-4 mr-1" />
                      Yasağı Kaldır
                    </>
                  ) : (
                    <>
                      <Ban className="w-4 h-4 mr-1" />
                      Yasakla
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmModal({ open: true, type: "delete", user })}
                  className="text-red-600 border-red-200"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Sil
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <h2 className="text-lg font-semibold text-slate-700 mb-4">Firma Kullanıcıları ({companyUsers.length})</h2>
      {companyUsers.length === 0 ? (
        <div className="text-center py-8 bg-stone-50 rounded-2xl">
          <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Firma kullanıcısı bulunmuyor</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {companyUsers.map((user) => (
            <div key={user.user_id} className={`dashboard-card ${user.is_banned ? "border-red-200 bg-red-50" : ""}`}>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-indigo-500" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-slate-800">{user.name}</h3>
                    {user.is_banned && <Badge className="bg-red-100 text-red-800">Yasaklı</Badge>}
                  </div>
                  <p className="text-sm text-slate-500">{user.email}</p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-stone-100 flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmModal({ open: true, type: "ban", user })}
                  className={user.is_banned ? "text-green-600 border-green-200" : "text-amber-600 border-amber-200"}
                >
                  {user.is_banned ? (
                    <>
                      <UserCheck className="w-4 h-4 mr-1" />
                      Yasağı Kaldır
                    </>
                  ) : (
                    <>
                      <Ban className="w-4 h-4 mr-1" />
                      Yasakla
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmModal({ open: true, type: "delete", user })}
                  className="text-red-600 border-red-200"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Sil
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={confirmModal.open} onOpenChange={(open) => setConfirmModal({ ...confirmModal, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirmModal.type === "ban"
                ? confirmModal.user?.is_banned
                  ? "Yasağı Kaldır"
                  : "Yasakla"
                : "Sil"}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              {confirmModal.type === "ban"
                ? confirmModal.user?.is_banned
                  ? `${confirmModal.user?.name} kullanıcısının yasağını kaldırmak istiyor musunuz?`
                  : `${confirmModal.user?.name} kullanıcısını yasaklamak istiyor musunuz?`
                : `${confirmModal.user?.name} kullanıcısını silmek istiyor musunuz? Bu işlem geri alınamaz.`}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmModal({ open: false, type: "", user: null })}>
              İptal
            </Button>
            <Button
              onClick={() =>
                confirmModal.type === "ban" ? handleBan(confirmModal.user?.user_id) : handleDelete(confirmModal.user?.user_id)
              }
              className={confirmModal.type === "delete" ? "bg-red-500 hover:bg-red-600" : "bg-orange-500 hover:bg-orange-600"}
            >
              {confirmModal.type === "ban" ? (confirmModal.user?.is_banned ? "Yasağı Kaldır" : "Yasakla") : "Sil"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const AdminDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      console.error(e);
    }
    localStorage.removeItem("session_token");
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <Sidebar user={user} onLogout={handleLogout} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <main className="lg:ml-64 p-4 lg:p-8 pt-16 lg:pt-8">
        <Routes>
          <Route index element={<AdminHome user={user} />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="reports" element={<AdminReports />} />
          <Route path="pending-companies" element={<PendingCompanies />} />
          <Route path="companies" element={<AdminCompanies />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="create" element={<AdminCreate />} />
          <Route path="settings" element={<AdminSettings />} />
        </Routes>
      </main>
    </div>
  );
};

export default AdminDashboard;
